'use client';

import { useCallback, useRef, useState } from 'react';
import { API_ORIGIN, resolveApiPath } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { OfflineAvatarInput, ViduV1GetTaskStateReply } from '@/app/types';
import {
  type CreateOfflineAvatarTaskOptions,
  createOfflineAvatarTask,
  createTtsTask,
  getTtsTaskResult,
  TtsGenerationError,
  type TtsResult,
} from '../apis';

const TTS_TASK_STATE_URL = resolveApiPath('/vidu/v1/tasks/state');
const TTS_TASK_TIMEOUT_MS = 5 * 60 * 1000;

interface PendingTtsTask {
  taskId: string;
  resolve: (result: TtsResult) => void;
  reject: (error: unknown) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export function useOfflineAvatarTasks() {
  const addNewTask = useStore((state) => state.addNewTask);
  const [submitting, setSubmitting] = useState(false);
  const [generatingSpeech, setGeneratingSpeech] = useState(false);
  const submitLock = useRef(false);
  const speechLock = useRef(false);
  const pendingTtsTaskRef = useRef<PendingTtsTask | null>(null);

  const { run: watchTtsTaskState, close: closeTtsTaskState } = useFetchStream<{ id: string }>(TTS_TASK_STATE_URL, {
    baseURL: API_ORIGIN,
    maxReconnectAttempts: 0,
    onMessage: (message) => {
      for (const chunk of message.split('data:')) {
        try {
          const state = JSON.parse(chunk.trim()) as ViduV1GetTaskStateReply & { err_msg?: string };
          const pendingTask = pendingTtsTaskRef.current;
          if (!pendingTask || !state.state) continue;

          if (state.state === 'success') {
            clearTimeout(pendingTask.timeoutId);
            pendingTtsTaskRef.current = null;
            closeTtsTaskState();
            void getTtsTaskResult(pendingTask.taskId).then(pendingTask.resolve, pendingTask.reject);
            continue;
          }

          if (state.state === 'failed' || state.state === 'canceled') {
            clearTimeout(pendingTask.timeoutId);
            pendingTtsTaskRef.current = null;
            closeTtsTaskState();
            pendingTask.reject(new TtsGenerationError(state.err_code, state.err_msg));
          }
        } catch {
          // SSE may contain heartbeat or partial chunks.
        }
      }
    },
    onClose: () => {
      const pendingTask = pendingTtsTaskRef.current;
      if (pendingTask) watchTtsTaskState({ id: pendingTask.taskId });
    },
    onError: (error) => {
      const pendingTask = pendingTtsTaskRef.current;
      if (!pendingTask) return;

      clearTimeout(pendingTask.timeoutId);
      pendingTtsTaskRef.current = null;
      closeTtsTaskState();
      pendingTask.reject(new TtsGenerationError(undefined, error.message));
    },
  });

  const generateTts = useCallback(
    async (script: string, voiceId: string) => {
      if (speechLock.current) return null;
      speechLock.current = true;
      setGeneratingSpeech(true);
      try {
        const task = await createTtsTask(script, voiceId);
        if (task.state === 'failed' || task.state === 'canceled') {
          throw new TtsGenerationError(task.err_code, task.err_msg);
        }
        if (task.state === 'success') {
          return await getTtsTaskResult(task.id);
        }

        return await new Promise<TtsResult>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            const pendingTask = pendingTtsTaskRef.current;
            if (pendingTask?.taskId !== task.id) return;

            pendingTtsTaskRef.current = null;
            closeTtsTaskState();
            reject(new TtsGenerationError());
          }, TTS_TASK_TIMEOUT_MS);
          pendingTtsTaskRef.current = { taskId: task.id, resolve, reject, timeoutId };
          watchTtsTaskState({ id: task.id });
        });
      } finally {
        const pendingTask = pendingTtsTaskRef.current;
        if (pendingTask) clearTimeout(pendingTask.timeoutId);
        pendingTtsTaskRef.current = null;
        closeTtsTaskState();
        speechLock.current = false;
        setGeneratingSpeech(false);
      }
    },
    [closeTtsTaskState, watchTtsTaskState],
  );

  const submit = useCallback(
    async (input: OfflineAvatarInput, options?: CreateOfflineAvatarTaskOptions) => {
      if (submitLock.current) return null;
      submitLock.current = true;
      setSubmitting(true);
      try {
        const task = await createOfflineAvatarTask(input, options);
        addNewTask(task);
        return task;
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    },
    [addNewTask],
  );

  return { generatingSpeech, generateTts, submitting, submit };
}
